import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  useToast,
  useDisclosure,
  Spinner,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import ClassModal from './ClassModal';
import SearchControls from './components/SearchControls';
import PaginationControls from './components/PaginationControls';
import api from '../../../../utils/api';
import analytics from '../../../../segmentTracking';

const ClassesTab = ({ refreshTrigger }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentClass, setCurrentClass] = useState(null);
  const [deleteClass, setDeleteClass] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalClasses, setTotalClasses] = useState(0);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  const toast = useToast();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const cancelRef = React.useRef();

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get('/dashboard/course-classes', {
        params: {
          institution: currentInstitution?._id,
          skip: skip,
          limit: itemsPerPage,
          search: searchTerm,
        },
      });

      setClasses(response.data.courseClasses || []);
      setTotalClasses(response.data.total || 0);

      analytics.track('settings:academic:classes:viewed', {
        classesCount: response.data.courseClasses?.length || 0,
        totalClasses: response.data.total || 0,
        currentPage,
        itemsPerPage,
        searchTerm: searchTerm,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || 'Failed to fetch classes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    currentInstitution,
    currentUser,
    toast,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (currentInstitution?._id) {
      fetchClasses();
    }
  }, [fetchClasses, currentInstitution]);

  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && currentInstitution?._id) {
      fetchClasses();
    }
  }, [refreshTrigger, fetchClasses, currentInstitution]);

  // Handlers for SearchControls and PaginationControls
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Class CRUD operations
  const handleAddClass = () => {
    setCurrentClass(null);
    onModalOpen();

    analytics.track('settings:academic:classes:addclicked', {
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  const handleEditClass = (courseClass) => {
    setCurrentClass(courseClass);
    onModalOpen();

    analytics.track('settings:academic:classes:editclicked', {
      classId: courseClass._id,
      className: courseClass.name,
      courseId: courseClass.course?._id,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  const handleDeleteClass = (courseClass) => {
    setDeleteClass(courseClass);
    onDeleteOpen();

    analytics.track('settings:academic:classes:deleteclicked', {
      classId: courseClass._id,
      className: courseClass.name,
      courseId: courseClass.course?._id,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  const confirmDeleteClass = async () => {
    if (!deleteClass) return;

    try {
      await api.delete(`/dashboard/course-classes/${deleteClass._id}`);

      toast({
        title: 'Success',
        description: 'Class deleted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      analytics.track('settings:academic:classes:deleted', {
        classId: deleteClass._id,
        className: deleteClass.name,
        courseId: deleteClass.course?._id,
        userRole: currentUser?.role,
        email: currentUser?.email,
      });

      // Refresh the list
      await fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to delete class',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setDeleteClass(null);
    }
  };

  const handleClassSaved = async () => {
    // Refresh the classes list
    await fetchClasses();
  };

  return (
    <Box>
      {/* Search Controls */}
      <SearchControls
        searchPlaceholder="Search classes..."
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onSearchChange={handleSearchChange}
        entityName="classes"
        analyticsPrefix="academic:classes"
        loading={loading}
      >
        <Button
          bg="#EF6506"
          color="white"
          _hover={{ bg: '#D55A05' }}
          onClick={handleAddClass}
        >
          Add Class
        </Button>
      </SearchControls>

      {/* Classes List */}
      <Box>
        {loading ? (
          <Flex justifyContent="center" alignItems="center" py={8}>
            <Spinner size="lg" color="#EF6506" />
          </Flex>
        ) : classes.length > 0 ? (
          <Flex flexDir="column" gap={2}>
            {classes.map((courseClass) => (
              <Flex
                key={courseClass._id}
                justifyContent="space-between"
                alignItems="center"
                p={4}
                bg="white"
                border="1px solid #E7E7E7"
                borderRadius="8px"
                _hover={{ bg: '#F9F9F9' }}
              >
                <Box flex="1">
                  <Text fontSize="16px" fontWeight="600" color="#222222" mb={1}>
                    {courseClass.name}
                  </Text>
                  <Text fontSize="14px" color="#757575" mb={1}>
                    Course: {courseClass.course?.name || 'N/A'}
                  </Text>
                  <Text fontSize="12px" color="#757575">
                    Created:{' '}
                    {new Date(courseClass.createdAt).toLocaleDateString()}
                  </Text>
                </Box>
                <Flex gap={2}>
                  <IconButton
                    aria-label="Edit class"
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => handleEditClass(courseClass)}
                  />
                  <IconButton
                    aria-label="Delete class"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteClass(courseClass)}
                  />
                </Flex>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Box textAlign="center" py={8}>
            <Text fontSize="16px" color="#757575">
              {searchTerm
                ? 'No classes found matching your search.'
                : 'No classes found. Add your first class to get started.'}
            </Text>
          </Box>
        )}
      </Box>

      {/* Pagination Controls */}
      <PaginationControls
        currentPage={currentPage}
        totalItems={totalClasses}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        entityName="classes"
        analyticsPrefix="academic:classes"
        loading={loading}
      />

      {/* Add/Edit Class Modal */}
      <ClassModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        courseClass={currentClass}
        onClassSaved={handleClassSaved}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Class
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{deleteClass?.name}"? This action
              cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteClass} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ClassesTab;
